import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { FormLabel, Input, Checkbox, getCheckboxCheckedValue } from '@infologistics/frontend-libraries';

import { getErrorText, hasError } from '@utils/utils';
import { RouteName, UserField } from '@const/consts';
import { IEmailInputWrapperProps as IProps } from './types';

import styles from '@views/auth/components/AuthNew.module.scss';
import { ICON_PROPS } from '../../../consts';
import { LoginButtons } from '../../../LoginButtons';
import { AuthTranslations } from '@app/const/translations';

const EmailInputWrapper: FC<IProps> = (props) => {
  const { errors, isSending, touched, values, handleChange, onPhoneLoginClick, onSSOLoginClick, onServerErrorChange } = props;

  const { t } = useTranslation();

  const errorText = {
    [UserField.EMAIL]: t(getErrorText(UserField.EMAIL, errors)),
    [UserField.PASSWORD]: t(getErrorText(UserField.PASSWORD, errors)),
  };

  const isError = {
    [UserField.EMAIL]: hasError(UserField.EMAIL, errors, touched),
    [UserField.PASSWORD]: hasError(UserField.PASSWORD, errors, touched),
  };
  const isDisabled = isError[UserField.EMAIL] || isError[UserField.PASSWORD] || isSending;

  const handleLoginByPhoneClick = (): void => {
    onPhoneLoginClick();
    onServerErrorChange(undefined);
  };

  const handleLoginBySSOClick = (): void => {
    onSSOLoginClick();
    onServerErrorChange(undefined);
  };

  return (
    <>
      <div className='mb-3'>
        <FormLabel htmlFor={UserField.EMAIL} text={t('auth:email')} className={cn(styles.label, 'text-muted')} />
        <Input
          id={UserField.EMAIL}
          name={UserField.EMAIL}
          onChange={handleChange}
          hasError={isError[UserField.EMAIL]}
          errorText={errorText[UserField.EMAIL]}
          value={values[UserField.EMAIL] ?? ''}
          rightIcon='IconEnvelopSolid'
          rightIconProps={{ ...ICON_PROPS, classes: styles.input_icon }}
          inputSize='md'
          classes={{ wrapper: cn(styles.input), input: cn(styles.inputs, styles.input_radius) }}
        />
      </div>

      <div className='mb-3'>
        <FormLabel htmlFor={UserField.PASSWORD} text={t('auth:password')} className={cn(styles.label, 'text-muted')} />
        <Input
          id={UserField.PASSWORD}
          name={UserField.PASSWORD}
          onChange={handleChange}
          hasError={isError[UserField.PASSWORD]}
          errorText={errorText[UserField.PASSWORD]}
          type='password'
          value={values[UserField.PASSWORD] ?? ''}
          rightIcon='IconLock'
          rightIconProps={{ ...ICON_PROPS, classes: styles.input_icon }}
          inputSize='md'
          classes={{ wrapper: cn(styles.input), input: cn(styles.inputs, styles.input_radius) }}
        />
      </div>

      <div className='d-flex justify-content-between mb-2'>
        <FormLabel text={t(AuthTranslations.remember)} className={cn(styles.label, 'd-inline-flex flex-row-reverse align-items-center')}>
          <Checkbox
            name='remember'
            checked={getCheckboxCheckedValue(values.remember)}
            onChange={handleChange}
            className={cn('mr-2 text-nowrap', styles.input_radius)}
            size='large'
          />
        </FormLabel>

        <Link to={RouteName.FORGOT} className={styles.textColored}>
          {t(AuthTranslations.forgot)}
        </Link>
      </div>

      <LoginButtons
        isDisabled={isDisabled}
        main={{
          text: AuthTranslations.signIn,
        }}
        left={{
          onClick: handleLoginByPhoneClick,
          text: AuthTranslations.loginByPhone,
        }}
        right={{
          onClick: handleLoginBySSOClick,
          text: AuthTranslations.loginBySSO,
        }}
      />
    </>
  );
};

export default EmailInputWrapper;
